import { Skeleton } from "@/components/shared/Skeleton";
import type { ProjectInput } from "@/generated/graphql";
import { THUMBNAIL } from "@/lib/constants/shared";
import { useProducts } from "@/lib/hooks/products/useProducts";
import { useAddProject } from "@/lib/hooks/projects/useAddProject";
import { useProjectsById } from "@/lib/hooks/projects/useProjectsById";
import { useUpdateProject } from "@/lib/hooks/projects/useUpdateProject";
import { useSchools } from "@/lib/hooks/schools/useSchool";
import { productLevelLabel, productLevelOptions } from "@/lib/utils/productLevel";
import { scrollToItem } from "@/lib/utils/scrollToItem";
import type { ProjectFields } from "@/types/Project";
import { Button, FormField, LoadMoreButton, Modal, SearchBar } from "@ai-teach/ui-web";
import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { MdAdd, MdCheckBox, MdCheckBoxOutlineBlank, MdClose } from "react-icons/md";
import { ImageUpload } from "../ImageUpload";
import { type ProductOption, ProductPickerModal } from "./ProductPickerModal";

/** No level chosen yet. Not 0 — that is a real level on products with a base level. */
const UNSET_LEVEL = -1;

export interface School {
  id: string;
  name: string;
}

export interface AddProjectModalProps {
  isOpen: boolean;
  onClose: () => void;
  parentId: string | null;
  project?: ProjectFields; // Optional: if provided, modal is in edit mode
}

export function AddProjectModal({ isOpen, onClose, parentId, project }: AddProjectModalProps) {
  const isEditMode = !!project;

  const [formData, setFormData] = useState<ProjectInput>({
    isHidden: project?.isHidden ?? false,
    isPublic: project?.isPublic ?? true,
    name: project?.name || "",
    parentId: project?.parentId || parentId || null,
    productIds: project?.productIds ?? [],
    schoolIds: project?.schoolIds || [],
    level: project?.level ?? UNSET_LEVEL,
  });

  const [uiState, setUiState] = useState({
    schoolSearch: "",
    showSchoolSelector: false,
  });

  const [showProductPicker, setShowProductPicker] = useState(false);

  const [fieldErrors, setFieldErrors] = useState({
    name: false,
    productIds: false,
    level: false,
    schools: false,
  });

  const [selectedImageFile, setSelectedImageFile] = useState<File | null>(null);
  const [removeImage, setRemoveImage] = useState(false);

  const { addProject, loading: addProjectLoading } = useAddProject();
  const { updateProject, loading: updateProjectLoading } = useUpdateProject();

  const loading = addProjectLoading || updateProjectLoading;

  const {
    schools,
    loading: schoolsLoading,
    loadMore: loadMoreSchools,
    hasMore: hasMoreSchools,
  } = useSchools(uiState.schoolSearch);

  // Only used to resolve names/levels of already-selected products; browsing happens in the picker.
  const { products, loading: productsLoading } = useProducts("");

  const { project: parentProject } = useProjectsById((parentId || project?.parentId) ?? null);

  const [seenProducts, setSeenProducts] = useState<Map<string, ProductOption>>(new Map());

  // Chips and the level bound describe the selection, which can include products the current
  // page of results no longer contains, so they read from everything seen.
  useEffect(() => {
    setSeenProducts((prev) => {
      const unseen = products.filter((product) => !prev.has(product.id));
      if (unseen.length === 0) return prev;
      const next = new Map(prev);
      for (const product of unseen) next.set(product.id, product);
      return next;
    });
  }, [products]);

  const selectedProducts = (formData.productIds ?? [])
    .map((id) => seenProducts.get(id))
    .filter((product) => product !== undefined);

  const unresolvedProductIds = (formData.productIds ?? []).filter((id) => !seenProducts.has(id));

  // An id is only unresolved until the catalog answers; after that it means the product was
  // deleted in MyBestUser, which leaves its id on the project. Either way the field must not
  // read as an empty selection.
  const pendingProductIds = productsLoading ? unresolvedProductIds : [];
  const unavailableProductIds = productsLoading ? [] : unresolvedProductIds;

  // Levels span every selected product, and a child may sit above its parent: a series folder
  // is level 1 so every student can open it, while the books inside carry levels 1-10. Going
  // beyond a shallower product's own range hides the project from everyone entitled through
  // that product.
  const levelOptions = [
    ...new Set(selectedProducts.flatMap((product) => productLevelOptions(product))),
  ].sort((a, b) => a - b);

  // Products name their levels differently, so the list follows the one picked first.
  const namingProduct = selectedProducts[0];

  // Children inherit their parent's products, so only a root project may change them.
  const productsLocked = Boolean(parentId || project?.parentId);

  const removeProductId = (productId: string) => {
    setFormData((prev) => ({
      ...prev,
      productIds: (prev.productIds ?? []).filter((id) => id !== productId),
      level: UNSET_LEVEL,
    }));
    if (fieldErrors.productIds) {
      setFieldErrors((prev) => ({ ...prev, productIds: false }));
    }
  };

  const handleProductToggle = (product: ProductOption) => {
    if ((formData.productIds ?? []).includes(product.id)) {
      removeProductId(product.id);
      return;
    }
    setSeenProducts((prev) =>
      prev.has(product.id) ? prev : new Map(prev).set(product.id, product)
    );
    setFormData((prev) => ({
      ...prev,
      productIds: [...(prev.productIds ?? []), product.id],
      level: UNSET_LEVEL,
    }));
    if (fieldErrors.productIds) {
      setFieldErrors((prev) => ({ ...prev, productIds: false }));
    }
  };

  // Sync form state when project prop changes (edit mode)
  useEffect(() => {
    if (!project) return;
    setFormData({
      isHidden: project.isHidden,
      isPublic: project.isPublic,
      name: project.name,
      parentId: project.parentId,
      productIds: project.productIds,
      schoolIds: project.schoolIds,
      level: project.level,
    });
    setUiState({
      schoolSearch: "",
      showSchoolSelector: false,
    });
  }, [project]);

  // Sync form state when project create and have parent project (create mode)
  useEffect(() => {
    if (isEditMode) return;
    if (!parentProject) return;
    setFormData((prev) => ({
      ...prev,
      productIds: parentProject.productIds,
      level: parentProject.level,
    }));
  }, [isEditMode, parentProject]);

  // Filter schools based on search
  const filteredSchools = schools.filter((school) =>
    school.name.toLowerCase().includes(uiState.schoolSearch.toLowerCase())
  );

  const handleSchoolToggle = (schoolId: string) => {
    setFormData((prev) => ({
      ...prev,
      schoolIds: prev.schoolIds?.includes(schoolId)
        ? prev.schoolIds.filter((id) => id !== schoolId)
        : [...(prev.schoolIds || []), schoolId],
    }));
    if (fieldErrors.schools) {
      setFieldErrors((prev) => ({ ...prev, schools: false }));
    }
  };

  const handleSubmit = async () => {
    // Validate required fields
    const errors = {
      name: !formData.name.trim(),
      // A deleted product entitles nobody, so saving it back would keep the project unreachable.
      // Children can only inherit theirs, so the fix belongs on the root project.
      productIds:
        !formData.productIds?.length || (!productsLocked && unavailableProductIds.length > 0),
      level: formData.level === UNSET_LEVEL,
      schools: !formData.isPublic && (!formData.schoolIds || formData.schoolIds.length === 0),
    };

    setFieldErrors(errors);

    // Check if any errors exist
    if (Object.values(errors).some((error) => error)) {
      return;
    }

    try {
      const projectData = {
        ...formData,
        name: formData.name.trim(),
      };

      if (isEditMode && project) {
        // Send file directly to backend - backend handles upload/deletion/cleanup
        await updateProject(project.id, projectData, selectedImageFile, removeImage);
        toast.success("Project updated successfully!");
      } else {
        // Send file directly to backend - backend handles upload/cleanup
        const newProjects = await addProject(projectData, selectedImageFile);
        if (newProjects?.[0]) scrollToItem("project", newProjects[0].id);
      }
      handleClose();
    } catch (error) {
      console.error(`Failed to ${isEditMode ? "update" : "add"} project:`, error);
      toast.error(`Failed to ${isEditMode ? "update" : "add"} project. Please try again.`);
    }
  };

  const handleClose = () => {
    // A sub-modal owns Escape and overlay clicks while it is open; closing the form under it
    // would throw away everything the user has typed.
    if (showProductPicker) return;

    // Reset form
    setFormData({
      isHidden: false,
      isPublic: true,
      name: "",
      parentId: parentId || null,
      productIds: [],
      schoolIds: [],
      level: UNSET_LEVEL,
    });
    setUiState({
      schoolSearch: "",
      showSchoolSelector: false,
    });
    setFieldErrors({
      name: false,
      productIds: false,
      level: false,
      schools: false,
    });
    setRemoveImage(false);
    onClose();
  };

  const selectedSchoolObjects = schools.filter((school) => formData.schoolIds?.includes(school.id));

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={isEditMode ? "Edit Project" : "Add Project"}
        size="lg"
      >
        {/* Action Buttons */}
        <div className="flex justify-end space-x-3 pt-4">
          <Button variant="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSubmit} disabled={loading}>
            {loading ? (isEditMode ? "Saving..." : "Adding...") : isEditMode ? "Save" : "Add"}
          </Button>
        </div>
        <div className="space-y-6">
          {/* Access Control Section */}
          <div>
            <div
              className={`block text-sm font-medium mb-3 ${fieldErrors.schools ? "text-red-500" : "text-secondary-200"}`}
            >
              <span className="text-red-500">*</span> Access Control
            </div>
            <div className="space-y-2">
              <label className="flex items-center space-x-2 cursor-pointer w-fit pr-3">
                <input
                  type="radio"
                  name="accessControl"
                  value="all"
                  checked={formData.isPublic}
                  onChange={() => {
                    setFormData((prev) => ({
                      ...prev,
                      isPublic: true,
                      schoolIds: [],
                    }));
                    if (fieldErrors.schools) {
                      setFieldErrors((prev) => ({ ...prev, schools: false }));
                    }
                  }}
                  className="sr-only"
                />
                {formData.isPublic ? (
                  <MdCheckBox className="w-5 h-5 text-success-500" />
                ) : (
                  <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm text-secondary-200">All schools</span>
              </label>
              <label className="flex items-center space-x-2 cursor-pointer w-fit pr-3">
                <input
                  type="radio"
                  name="accessControl"
                  value="selected"
                  checked={!formData.isPublic}
                  onChange={() => setFormData((prev) => ({ ...prev, isPublic: false }))}
                  className="sr-only"
                />
                {!formData.isPublic ? (
                  <MdCheckBox className="w-5 h-5 text-success-500" />
                ) : (
                  <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
                )}
                <span className="text-sm text-secondary-200">Selected schools</span>
              </label>
            </div>
          </div>

          {/* School Selection (shown when "Selected schools" is chosen) */}
          {!formData.isPublic && (
            <div
              className={`border rounded-lg p-4 space-y-4 ${fieldErrors.schools ? "border-red-500" : "border-gray-800"}`}
            >
              <div className="flex items-center justify-between">
                <div className="block label">
                  <span className="text-red-500">*</span> Select the schools
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() =>
                    setUiState((prev) => ({
                      ...prev,
                      showSchoolSelector: !prev.showSchoolSelector,
                    }))
                  }
                >
                  {uiState.showSchoolSelector ? "Hide" : "Show"} selector
                </Button>
              </div>

              {/* Selected Schools Display */}
              {selectedSchoolObjects.length > 0 && (
                <div className="grid grid-cols-3 gap-2">
                  {selectedSchoolObjects.map((school) => (
                    <label key={school.id} className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked
                        onChange={() => handleSchoolToggle(school.id)}
                        className="sr-only"
                      />
                      <div>
                        <MdCheckBox className="w-5 h-5 text-success-500" />
                      </div>
                      <span className="text-sm text-secondary-200">{school.name}</span>
                    </label>
                  ))}
                </div>
              )}

              {/* School Selector */}
              {uiState.showSchoolSelector && (
                <div className="space-y-3">
                  <SearchBar
                    value={uiState.schoolSearch}
                    onChange={(value: string) =>
                      setUiState((prev) => ({ ...prev, schoolSearch: value }))
                    }
                    placeholder="Search for school"
                    className="w-full"
                  />

                  <div className="space-y-2 max-h-60 overflow-y-auto gradient-scrollbar">
                    <p className="caption font-medium uppercase">School</p>
                    <div className="grid grid-cols-3 gap-2">
                      {filteredSchools.map((school) => (
                        <label
                          key={school.id}
                          className="flex items-center space-x-2 cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={formData.schoolIds?.includes(school.id) || false}
                            onChange={() => handleSchoolToggle(school.id)}
                            className="sr-only"
                          />
                          <div>
                            {formData.schoolIds?.includes(school.id) ? (
                              <MdCheckBox className="w-5 h-5 text-success-500" />
                            ) : (
                              <MdCheckBoxOutlineBlank className="w-5 h-5 text-gray-400" />
                            )}
                          </div>
                          <span className="text-sm text-secondary-200 leading-tight">
                            {school.name}
                          </span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {true && (
                    <LoadMoreButton
                      onClick={loadMoreSchools}
                      hasMore={hasMoreSchools}
                      loading={schoolsLoading}
                      defaultText="Load more"
                    />
                  )}
                </div>
              )}
            </div>
          )}

          <div className="flex gap-6">
            {/* Image Upload - Left Side */}
            <div className="flex-1">
              <label htmlFor="image" className="block label mb-2">
                Thumbnail Image
              </label>
              <ImageUpload
                onFileSelect={(file) => {
                  setSelectedImageFile(file);
                  setRemoveImage(false);
                }}
                onRemove={() => {
                  setSelectedImageFile(null);
                  setRemoveImage(true);
                }}
                currentImageUrl={project?.imageUrl ?? null}
                aspectRatio={THUMBNAIL.projectAspectRatio}
              />
            </div>

            {/* Form Fields - Right Side */}
            <div className="flex-1 space-y-4">
              {/* Product Selector */}
              <div>
                <label className="block label mb-1">
                  <span className="text-red-500">*</span> Product
                </label>
                <div
                  className={`flex flex-wrap items-center gap-2 border rounded-lg bg-surface px-2 py-2 min-h-[42px] ${fieldErrors.productIds ? "border-red-500" : "border-gray-800"}`}
                >
                  {selectedProducts.length === 0 && unresolvedProductIds.length === 0 && (
                    <span className="text-sm text-secondary-400 px-1">
                      {productsLocked ? "Inherited from parent" : "No product selected"}
                    </span>
                  )}
                  {pendingProductIds.map((productId) => (
                    <Skeleton key={productId} className="h-7 w-28 rounded-full bg-gray-700" />
                  ))}
                  {unavailableProductIds.map((productId) => (
                    <span
                      key={productId}
                      title={productId}
                      className="flex items-center gap-1 py-1 pl-3 pr-2 rounded-full text-sm font-medium text-warning-700 dark:text-warning-100 bg-warning-500/15 border border-warning-500/50"
                    >
                      Unavailable product
                      {!productsLocked && (
                        <button
                          type="button"
                          aria-label={`Remove unavailable product ${productId}`}
                          onClick={() => removeProductId(productId)}
                          className="text-warning-700 dark:text-warning-100 hover:text-error-500"
                        >
                          <MdClose size={16} />
                        </button>
                      )}
                    </span>
                  ))}
                  {selectedProducts.map((product) => (
                    <span
                      key={product.id}
                      className="flex items-center gap-1 py-1 pl-3 pr-2 rounded-full text-sm text-secondary-200 bg-primary-500/10 border border-primary-500/30"
                    >
                      {product.name}
                      {!productsLocked && (
                        <button
                          type="button"
                          aria-label={`Remove ${product.name}`}
                          onClick={() => handleProductToggle(product)}
                          className="text-secondary-400 hover:text-red-500"
                        >
                          <MdClose size={16} />
                        </button>
                      )}
                    </span>
                  ))}
                  {!productsLocked && (
                    <button
                      type="button"
                      aria-label="Add product"
                      onClick={() => setShowProductPicker(true)}
                      className="flex items-center justify-center w-7 h-7 rounded-full border border-dashed border-gray-600 text-secondary-400 transition-colors hover:border-primary-500 hover:text-primary-500"
                    >
                      <MdAdd size={18} />
                    </button>
                  )}
                </div>
              </div>

              {/* Level Dropdown - only shown when product is selected */}
              {selectedProducts.length > 0 && (
                <div>
                  <label htmlFor="level" className="block label mb-1">
                    <span className="text-red-500">*</span> Level
                  </label>
                  <select
                    id="level"
                    value={formData.level === UNSET_LEVEL ? "" : formData.level}
                    onChange={(e) => {
                      setFormData((prev) => ({ ...prev, level: Number(e.target.value) }));
                      if (fieldErrors.level) {
                        setFieldErrors((prev) => ({ ...prev, level: false }));
                      }
                    }}
                    className={`mt-1 block w-full px-3 py-2 bg-surface border rounded-md text-secondary-200 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 cursor-pointer ${
                      fieldErrors.level ? "border-red-500" : "border-gray-800"
                    }`}
                    required
                  >
                    <option value="">Select level</option>
                    {levelOptions.map((level) => (
                      <option key={`level${level}`} value={level}>
                        {namingProduct ? productLevelLabel(namingProduct, level) : level}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Name Field */}
              <FormField
                id="name"
                name="name"
                label="Name"
                type="text"
                value={formData.name}
                onChange={(e) => {
                  setFormData((prev) => ({ ...prev, name: e.target.value }));
                  if (fieldErrors.name) {
                    setFieldErrors((prev) => ({ ...prev, name: false }));
                  }
                }}
                required
                placeholder="Enter project name"
                error={fieldErrors.name ? " " : undefined}
              />
            </div>
          </div>
        </div>
      </Modal>

      <ProductPickerModal
        isOpen={showProductPicker}
        onClose={() => setShowProductPicker(false)}
        selectedIds={formData.productIds ?? []}
        onToggle={handleProductToggle}
      />
    </>
  );
}
